/**
 * Driver pickup-point editor (Publish). Shows the interactive map when a
 * basemap is available; otherwise falls back to a label + "use my location"
 * (device GPS) so a pin can still be set on any device. Always optional.
 */
import { useT } from '../lib/i18n';
import { hapticTap } from '../lib/telegram';
import { CardHint } from './Card';
import { TextInput } from './TextInput';
import { PickupMap, mapAvailable } from './PickupMap';

export interface PickupValue {
  title: string;
  lat: number | null;
  lng: number | null;
}

interface Props {
  value: PickupValue;
  centerLat: number;
  centerLng: number;
  dark: boolean;
  onChange: (v: PickupValue) => void;
}

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

export function PickupField({ value, centerLat, centerLng, dark, onChange }: Props): JSX.Element {
  const { t } = useT();

  const useMyLocation = (): void => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        hapticTap();
        onChange({
          ...value,
          lat: round3(pos.coords.latitude),
          lng: round3(pos.coords.longitude),
        });
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-2">
      <TextInput
        id="pickup-title"
        label={t('pickup.label')}
        value={value.title}
        maxLength={80}
        placeholder={t('pickup.labelPh')}
        onChange={(e) => onChange({ ...value, title: e.target.value })}
      />
      {mapAvailable() ? (
        <>
          <CardHint>{t('pickup.tapHint')}</CardHint>
          <PickupMap
            lat={value.lat}
            lng={value.lng}
            centerLat={centerLat}
            centerLng={centerLng}
            editable
            dark={dark}
            onPick={(lat, lng) => onChange({ ...value, lat, lng })}
          />
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={useMyLocation}
            className="w-full rounded-input bg-tg-bg-secondary py-2.5 text-sm font-medium text-tg-text"
          >
            📍 {t('pickup.useLocation')}
          </button>
          <CardHint>
            {value.lat != null && value.lng != null ? t('pickup.pinSet') : t('pickup.optionalHint')}
          </CardHint>
        </>
      )}
    </div>
  );
}
